/**
 * markerPopup.ts — the single owner of "the marker popup that is currently open" (T-435).
 *
 * THE BUG THIS EXISTS TO KILL. Marker popups used to be created ad hoc — a bare
 * `new maplibregl.Popup(...).addTo(map)` inside whichever click handler happened to run. MapLibre
 * dispatches a layer-scoped click handler for EVERY layer under the cursor, so a single user click
 * can run several handlers that know nothing about each other, and each one adds its own popup.
 * Ricardo's screenshot is the result: a single-POI tooltip and a "2 nearby" cluster popup stacked
 * on the same pixel, the lower one unreadable.
 *
 * MapLibre's own `closeOnClick` does not save us here, and it is worth knowing why before anyone
 * "simplifies" this away: that listener is registered while the popup is being added — i.e. DURING
 * the dispatch of the very click that created it — so it does not fire for that click. It closes
 * the popup on the NEXT click, which is exactly why sequential clicks always looked fine and
 * overlapping markers did not.
 *
 * So "exactly one popup" is made a property of this module rather than a coincidence of how many
 * handlers fired: opening a popup closes whatever this map already had open. Callers do not need
 * to coordinate with each other, which matters because they live in different components
 * (POILayerMgr and EVChargerLayer) and neither can see the other's state.
 *
 * Keyed by map in a WeakMap rather than a module-level singleton so that multiple maps (and, just
 * as importantly, parallel test files) cannot leak popup state into each other, and so a discarded
 * map does not keep its last popup alive.
 */
import maplibregl from 'maplibre-gl'

/** The one popup currently open for a given map, if any. */
const openPopups = new WeakMap<maplibregl.Map, maplibregl.Popup>()

export interface MarkerPopupOptions {
  lngLat: maplibregl.LngLatLike
  html: string
  /** Defaults match the POI popup's previous inline options. */
  maxWidth?: string
  offset?: number
}

/**
 * Close whatever marker popup this map has open. Safe to call when there is none — teardown and
 * click paths both call it unconditionally.
 */
export function closeMarkerPopup(map: maplibregl.Map): void {
  const open = openPopups.get(map)
  if (!open) return
  openPopups.delete(map)
  open.remove()
}

/**
 * Open a marker popup, replacing this map's previous one. Returns the popup so a caller can hold
 * it if it needs to (nothing does today).
 */
export function openMarkerPopup(
  map: maplibregl.Map,
  options: MarkerPopupOptions,
): maplibregl.Popup {
  closeMarkerPopup(map)
  const popup = new maplibregl.Popup({
    maxWidth: options.maxWidth ?? '240px',
    offset: options.offset ?? 8,
  })
    .setLngLat(options.lngLat)
    .setHTML(options.html)
    .addTo(map)
  openPopups.set(map, popup)
  return popup
}
